import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiArrowLeft, FiCalendar, FiMapPin, FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';
import Loader from 'react-loader-spinner';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import EditPlayerModal from '../components/EditPlayerModal';
import AddPlayerToApplicationModal from '../components/AddPlayerToApplicationModal';
import AddCaptainModal from '../components/AddCaptainModal';
import DuplicatePlayersHint from '../components/DuplicatePlayersHint';
import { maskPhone } from '../utilities/phone';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/playerDetail.css';

export default function PlayerDetail() {
    const { playerId } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const userState = useSelector(state => state.user);
    const user = userState?.userData?.data || {};

    const [player, setPlayer] = useState(null);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddToApplicationModal, setShowAddToApplicationModal] = useState(false);

    // Капитанство: команды из related_teams
    const [captainTeams, setCaptainTeams] = useState([]);
    const [showAddCaptainModal, setShowAddCaptainModal] = useState(false);
    // Сервер отклонил назначение без телефона — просим ввести номер
    const [captainPhoneRequired, setCaptainPhoneRequired] = useState(false);

    useEffect(() => {
        loadPlayerData();
    }, [playerId]);

    const loadPlayerData = async () => {
        try {
            setLoading(true);
            setError(null);

            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }

            const tokens = getTokensFromStore();

            // Загружаем данные игрока
            const playerResponse = await axios.get(
                `${BASE_URL.url}/players/${playerId}`,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                    }
                }
            );

            const playerData = playerResponse.data?.data || playerResponse.data;
            setPlayer(playerData);

            // Загружаем заявки игрока
            try {
                const applicationsResponse = await axios.get(
                    `${BASE_URL.url}/players/${playerId}/adminApplications`,
                    {
                        headers: {
                            "Authorization": "Bearer " + tokens.access_token,
                        }
                    }
                );

                const apps = applicationsResponse.data?.data || applicationsResponse.data || [];
                setApplications(Array.isArray(apps) ? apps : []);
            } catch (error) {
                console.error('Error loading applications:', error);
                setApplications([]);
            }

            // Команды, где игрок числится представителем (related_teams)
            try {
                const teamsResponse = await axios.get(
                    `${BASE_URL.url}/players/${playerId}/adminTeams`,
                    {
                        headers: {
                            "Authorization": "Bearer " + tokens.access_token,
                        }
                    }
                );
                const teams = teamsResponse.data?.data || teamsResponse.data || [];
                setCaptainTeams(Array.isArray(teams) ? teams : []);
            } catch (error) {
                console.error('Error loading captain teams:', error);
                setCaptainTeams([]);
            }

        } catch (error) {
            console.error('Error loading player:', error);
            setError('Ошибка загрузки данных игрока');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        history.goBack();
    };

    const handleTeamClick = (teamId) => {
        history.push(`/teams_management/${teamId}`);
    };

    const handleEditPlayer = () => {
        // Проверяем права на редактирование
        // role == 7 - супер админ, может редактировать всех
        // role == 6 - админ лиги, может редактировать только своих игроков (client_owner совпадает)
        if (user.role === 7) {
            // Супер админ может редактировать любого игрока
            setShowEditModal(true);
        } else if (user.role === 6 && user.client_id === player.client_owner) {
            // Админ лиги может редактировать только игроков своей лиги
            setShowEditModal(true);
        } else {
            // Нет прав на редактирование
            alert('Вы можете менять данные только созданных Вашей лигой игроков. Для изменения данных обратитесь к разработчикам приложения');
        }
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
        loadPlayerData(); // Перезагружаем данные
    };

    const handleAddToApplication = () => {
        setShowAddToApplicationModal(true);
    };

    // Капитанство доступно всем админам и по любому игроку: мы не меняем
    // данные игрока, а лишь добавляем строку в related_teams своей команде.
    // Проверка владельца тут была лишней — админы регулярно назначают
    // капитанами людей, заведённых другой лигой
    const handleAddCaptain = () => {
        setCaptainPhoneRequired(false);
        setShowAddCaptainModal(true);
    };

    const handleCaptainSubmit = async (team, phone) => {
        try {
            setLoading(true);
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            const tokens = getTokensFromStore();

            await axios.post(
                `${BASE_URL.url}${API_URLS.setPlayerCaptain}`,
                // Игрок уже капитан другой команды — телефон у него есть,
                // и поле phone не отправляем совсем, чтобы сервер не затёр
                // существующий номер. Заново телефон нужен только тому,
                // кто становится капитаном впервые
                phone
                    ? {
                        playerId: Number(playerId),
                        teamId: team.team_id,
                        phone,
                    }
                    : {
                        playerId: Number(playerId),
                        teamId: team.team_id,
                    },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                    }
                }
            );

            loadPlayerData();
            setCaptainPhoneRequired(false);
            return true;
        } catch (requestError) {
            console.error('Error setting captain:', requestError);
            const serverMessage = requestError.response && requestError.response.data;
            const text = typeof serverMessage === 'string'
                ? serverMessage
                : (serverMessage && (serverMessage.message || serverMessage.error))
                    || requestError.message
                    || 'Не удалось назначить капитана';
            // Рут требует телефон даже у того, у кого номер уже записан.
            // Полный номер API не отдаёт (только маску), переслать
            // существующий нельзя — показываем поле и просим ввести вручную
            if (!phone) {
                setCaptainPhoneRequired(true);
            }
            alert(text);
            setLoading(false);
            return false;
        }
    };

    const handleAddSuccess = () => {
        setShowAddToApplicationModal(false);
        loadPlayerData(); // Перезагружаем данные
    };

    const handleRemoveFromApplication = async (app) => {
        const confirmDelete = window.confirm(
            `Вы уверены, что хотите удалить игрока из заявки "${app.application_name}" команды "${app.name || app.team_name}"?`
        );
        
        if (!confirmDelete) return;

        try {
            setLoading(true);
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            const tokens = getTokensFromStore();

            await axios.post(
                `${BASE_URL.url}/players/connectToApplication`,
                {
                    playerId: playerId,
                    add_data: [],
                    remove_data: [{ id: app.id, application_id: app.application_id }]
                },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                    }
                }
            );

            alert('Игрок успешно удален из заявки');
            loadPlayerData(); // Перезагружаем данные
        } catch (error) {
            console.error('Error removing from application:', error);
            alert('Ошибка при удалении из заявки');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="layoutWrapper">
                <div className="loaderWrapper">
                    <Loader type="Oval" color="#4A5568" height={80} width={80} />
                </div>
            </div>
        );
    }

    if (error || !player) {
        return (
            <div className="layoutWrapper">
                <div className="errorWrapper">
                    <h2>{error || 'Игрок не найден'}</h2>
                    <button onClick={handleBack} className="backButton">
                        ← Назад
                    </button>
                </div>
            </div>
        );
    }

    // Телефон приходит уже замаскированным (phone_masked); maskPhone —
    // запасной вариант, если пришёл полный номер
    const playerPhone = player.phone_masked || player.phone || null;
    const captaincyRows = captainTeams.map(team => ({
        team_id: Number(team.team_id !== undefined ? team.team_id : team.id),
        name: team.name || team.team_name || 'Команда',
        image: team.image || '',
        isCaptain: Number(team.capitan) === 1,
    }));

    // Телефон спрашиваем только у того, кто НИ РАЗУ не назначался капитаном.
    // Признак — наличие номера: players.phone заполняется ровно в момент
    // первого назначения, по нему капитан входит в приложение и получает
    // роль 3. Связи игрока с юзером сервер в /players/:id не отдаёт,
    // поэтому телефон — единственный доступный признак привязки.
    const alreadyCaptain = Boolean(playerPhone);

    const playerPhoto = player.image || player.photo || player.avatar;
    const birthday = player.birthday || player.birth_date || player.birthDate;
    const position = player.position || player.position_name;

    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                {/* Header */}
                <div className="matchCenterHeader">
                    <button onClick={handleBack} className="backButton">
                        <FiArrowLeft /> Назад
                    </button>
                    <span className="playerDetailTitle">Информация об игроке</span>
                </div>
                
                <div className="playerDetailContainer">

                {/* Player Card */}
                <div className="playerCardLarge">
                    <div className="playerPhotoSection">
                        <img 
                            src={
                                playerPhoto 
                                    ? (playerPhoto.startsWith('/') 
                                        ? BASE_URL.url + playerPhoto 
                                        : playerPhoto.startsWith('data:') 
                                            ? playerPhoto 
                                            : `data:image/jpeg;base64,${playerPhoto}`)
                                    : defaultTeamLogo
                            }
                            alt={player.family || 'Игрок'}
                            onError={(e) => { e.target.src = defaultTeamLogo; }}
                        />
                    </div>
                    
                    <div className="playerInfoSection">
                        <h2 className="playerFullName">
                            {player.family} {player.name} {player.father_name}
                        </h2>
                        {/* Другие профили с таким же ФИО — кандидаты на объединение */}
                        <DuplicatePlayersHint
                            player={{ ...player, player_id: player.id || player.player_id }}
                        />
                        
                        <div className="playerBasicInfo">
                            {position && (
                                <div className="infoBox">
                                    <span className="infoBoxLabel">Позиция</span>
                                    <span className="infoBoxValue">{position}</span>
                                </div>
                            )}
                            {birthday && (
                                <div className="infoBox">
                                    <span className="infoBoxLabel">Дата рождения</span>
                                    <span className="infoBoxValue">
                                        {new Date(birthday).toLocaleDateString('ru-RU')}
                                        {player.age && ` (${player.age} лет)`}
                                    </span>
                                </div>
                            )}
                            <div className="infoBox">
                                <span className="infoBoxLabel">ID</span>
                                <span className="infoBoxValue playerIdValue">{player.id || player.player_id}</span>
                            </div>
                        </div>

                        {/* Additional Info Grid */}
                        <div className="additionalInfoGrid">
                            {player.height && (
                                <div className="infoBox">
                                    <span className="infoBoxLabel">Рост</span>
                                    <span className="infoBoxValue">{player.height} см</span>
                                </div>
                            )}
                            {player.weight && (
                                <div className="infoBox">
                                    <span className="infoBoxLabel">Вес</span>
                                    <span className="infoBoxValue">{player.weight} кг</span>
                                </div>
                            )}
                            {player.nationality && (
                                <div className="infoBox">
                                    <span className="infoBoxLabel">Гражданство</span>
                                    <span className="infoBoxValue">{player.nationality}</span>
                                </div>
                            )}
                            {player.work_foot && (
                                <div className="infoBox">
                                    <span className="infoBoxLabel">Рабочая нога</span>
                                    <span className="infoBoxValue">{player.work_foot}</span>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="playerActions">
                            <button className="actionButton editButton" onClick={handleEditPlayer}>
                                <FiEdit /> Редактировать данные
                            </button>
                        </div>
                    </div>
                </div>

                {/* Applications Section */}
                <div className="applicationsSection">
                    <div className="sectionHeader">
                        <h2>Заявки игрока</h2>
                        <button className="addApplicationButton" onClick={handleAddToApplication}>
                            <FiPlus /> Добавить в заявку
                        </button>
                    </div>
                    
                    {loading ? (
                        <div className="loadingState">
                            <Loader type="Oval" color="#4A5568" height={40} width={40} />
                        </div>
                    ) : applications.length > 0 ? (
                        <div className="applicationsTable">
                            <div className="applicationsTableHeader">
                                <div className="tableCell logoCell">Лого</div>
                                <div className="tableCell teamNameCell">Команда</div>
                                <div className="tableCell appNameCell">Заявка</div>
                                <div className="tableCell numberCell">Номер</div>
                                <div className="tableCell actionsCell">Действия</div>
                            </div>
                            {applications.map((app, index) => {
                                const teamLogo = app.team_logo || app.image;
                                const teamName = app.team_name || app.name || 'Команда';
                                const appName = app.application_name || 'Заявка';
                                const appYear = app.application_year || '';
                                
                                return (
                                    <div key={index} className="applicationRow">
                                        <div className="tableCell logoCell">
                                            <img 
                                                src={
                                                    teamLogo 
                                                        ? (teamLogo.startsWith('/') 
                                                            ? BASE_URL.url + teamLogo 
                                                            : teamLogo.startsWith('data:') 
                                                                ? teamLogo 
                                                                : `data:image/jpeg;base64,${teamLogo}`)
                                                        : defaultTeamLogo
                                                }
                                                alt={teamName}
                                                className="teamLogoImg"
                                                onError={(e) => { e.target.src = defaultTeamLogo; }}
                                            />
                                        </div>
                                        <div className="tableCell teamNameCell">
                                            <span className="teamNameText">{teamName}</span>
                                            {app.league_name && (
                                                <span className="leagueName">{app.league_name}</span>
                                            )}
                                        </div>
                                        <div className="tableCell appNameCell">
                                            <span className="appNameText">{appName}</span>
                                            {appYear && (
                                                <span className="appYear">{appYear}</span>
                                            )}
                                        </div>
                                        <div className="tableCell numberCell">
                                            {app.player_number && (
                                                <span className="playerNumberBadge">#{app.player_number}</span>
                                            )}
                                        </div>
                                        <div className="tableCell actionsCell">
                                            <button 
                                                className="deleteButton"
                                                onClick={() => handleRemoveFromApplication(app)}
                                                title="Удалить из заявки"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="emptyApplications">
                            <p>Игрок пока не добавлен ни в одну заявку</p>
                            <button className="addApplicationButton" onClick={handleAddToApplication}>
                                <FiPlus /> Добавить в заявку
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Captaincy Section */}
                <div className="applicationsSection">
                    <div className="sectionHeader">
                        <h2>Капитанство</h2>
                        <button className="addApplicationButton" onClick={handleAddCaptain}>
                            <FiPlus /> Добавить как капитана
                        </button>
                    </div>

                    {captaincyRows.length > 0 ? (
                        <div className="applicationsTable">
                            <div className="applicationsTableHeader">
                                <div className="tableCell logoCell">Лого</div>
                                <div className="tableCell teamNameCell">Команда</div>
                                <div className="tableCell appNameCell">Телефон</div>
                                <div className="tableCell numberCell">Роль</div>
                            </div>
                            {captaincyRows.map(row => (
                                <div key={row.team_id} className="applicationRow">
                                    <div className="tableCell logoCell">
                                        <img
                                            src={row.image
                                                ? (row.image.startsWith('/')
                                                    ? BASE_URL.url + row.image
                                                    : row.image)
                                                : defaultTeamLogo}
                                            alt={row.name}
                                            className="teamLogoImg"
                                            onError={(e) => { e.target.src = defaultTeamLogo; }}
                                        />
                                    </div>
                                    <div className="tableCell teamNameCell">
                                        <span
                                            className="teamNameText teamNameLink"
                                            onClick={() => handleTeamClick(row.team_id)}
                                        >
                                            {row.name}
                                        </span>
                                    </div>
                                    <div className="tableCell appNameCell">
                                        {playerPhone ? (
                                            <span className="phoneMasked">{maskPhone(playerPhone)}</span>
                                        ) : (
                                            <span className="phoneMissing">номер не задан</span>
                                        )}
                                    </div>
                                    <div className="tableCell numberCell">
                                        <span className="captainBadge">
                                            {row.isCaptain ? 'капитан' : 'представитель'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="emptyApplications">
                            <p>Игрок не является представителем ни одной команды</p>
                            <button className="addApplicationButton" onClick={handleAddCaptain}>
                                <FiPlus /> Добавить как капитана
                            </button>
                        </div>
                    )}
                </div>

                {/* Modals */}
                {player && (
                    <>
                        <EditPlayerModal
                            isOpen={showEditModal}
                            onClose={() => setShowEditModal(false)}
                            onSuccess={handleEditSuccess}
                            player={player}
                        />
                        
                        <AddPlayerToApplicationModal
                            isOpen={showAddToApplicationModal}
                            onClose={() => setShowAddToApplicationModal(false)}
                            onSuccess={handleAddSuccess}
                            playerId={player.id || player.player_id}
                        />

                        <AddCaptainModal
                            isOpen={showAddCaptainModal}
                            onClose={() => setShowAddCaptainModal(false)}
                            onSubmit={handleCaptainSubmit}
                            playerName={`${player.family || ''} ${player.name || ''}`.trim()}
                            currentPhone={playerPhone}
                            skipPhone={alreadyCaptain}
                            phoneRequired={captainPhoneRequired}
                            takenTeamIds={captaincyRows.map(r => r.team_id)}
                        />
                    </>
                )}
            </div>
            </div>
        </div>
    );
}
